import { z } from 'zod';

/**
 * The standard response envelope (N10). Every api response — success or error —
 * is wrapped by the global interceptor / exception filter, so clients unwrap in
 * exactly one place (`baseQueryWithAuth`, W4/M4).
 */

/** Fields shared by both envelope shapes. */
const envelopeBase = {
  message: z.string().nullable(),
  path: z.string(),
  timestamp: z.iso.datetime(),
};

/**
 * Wraps a payload schema in the success envelope.
 *
 * @example
 * const MemberResponseSchema = makeSuccessEnvelopeSchema(MemberSchema);
 */
export function makeSuccessEnvelopeSchema<TData extends z.ZodType>(data: TData) {
  return z.object({
    success: z.literal(true),
    data,
    ...envelopeBase,
  });
}

export type SuccessEnvelope<TData> = {
  success: true;
  data: TData;
  message: string | null;
  path: string;
  timestamp: string;
};

/**
 * The standardized error shape. `code` is a stable machine-readable key clients
 * branch on; `message` is human-facing and already locale-resolved by the api.
 * `details` carries field-level validation errors for form mapping (W9).
 */
export const ApiErrorSchema = z.object({
  code: z.string().min(1),
  details: z.record(z.string(), z.array(z.string())).optional(),
});

export type ApiError = z.infer<typeof ApiErrorSchema>;

export const ErrorEnvelopeSchema = z.object({
  success: z.literal(false),
  error: ApiErrorSchema,
  ...envelopeBase,
});

export type ErrorEnvelope = z.infer<typeof ErrorEnvelopeSchema>;
